/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./styles/GenericTable.css";
import { ReactComponent as IconUpDown } from "../../shared/assets/Icons/IconsUpDown.svg";
import { SkeletonTableRows } from "../Skeleton/GenericSkeleton";

// Accepts "120", "120px", or returns 0 if unset/invalid
function widthToPx(w?: string): number {
  if (!w) return 0;
  const m = String(w).match(/^(\d+)(px)?$/);
  return m ? Number(m[1]) : 0;
}

function computeStickyOffsets<T>(cols: GenericTableColumn<T>[]) {
  const leftOffsets: number[] = Array(cols.length).fill(0);
  const rightOffsets: number[] = Array(cols.length).fill(0);

  // LEFT pass
  let leftAcc = 0;
  for (let i = 0; i < cols.length; i++) {
    const c = cols[i];
    if (c.fixed === "left") {
      leftOffsets[i] = leftAcc;
      leftAcc += widthToPx(c.width);
    }
  }

  // RIGHT pass
  let rightAcc = 0;
  for (let i = cols.length - 1; i >= 0; i--) {
    const c = cols[i];
    if (c.fixed === "right") {
      rightOffsets[i] = rightAcc;
      rightAcc += widthToPx(c.width);
    }
  }

  return { leftOffsets, rightAcc, rightOffsets, leftAcc };
}

// // Ajuste: header ahora es string | React.ReactNode
// Ajuste: header ahora es string | React.ReactNode
export interface GenericTableColumn<T> {
  id?: string;
  header: string | React.ReactNode;
  accessor?: keyof T;
  sortAccessor?: (
    row: T
  ) => string | number | Date | Array<string | number> | null | undefined;
  renderCell?: (
    row: T,
    rowIndex: number,
    toggleRowExpand?: (rowIndex: number) => void,
    isExpanded?: boolean
  ) => React.ReactNode;
  fixed?: "left" | "right"; // New prop to indicate if column should be fixed
  /**
   * Column width. Supports px (e.g., "120px"), "auto", or % (e.g., "10%").
   * Note: If using fixed columns (left/right), prefer px values for accurate sticky offset calculation.
   */
  width?: string;
  sortable?: boolean;
  /**
   * Control how text wraps in this column's cells.
   * - "nowrap": Single line, no wrapping (whitespace-nowrap)
   * - "truncate": Single line with ellipsis (truncate whitespace-nowrap)
   * - "wrap": Normal wrapping at word boundaries (whitespace-normal)
   * - "break": Wrap and break long words (whitespace-normal break-words)
   * - "clamp-2": Clamp to 2 lines maximum (line-clamp-2)
   * If not specified, uses default behavior based on table layout.
   */
  wrapMode?: "nowrap" | "truncate" | "wrap" | "break" | "clamp-2";
}

export interface GenericTableProps<T> {
  columns: GenericTableColumn<T>[];
  data: T[];

  tableClassName?: string;
  initialRowsPerPage?: number;
  rowsPerPageOptions?: number[];
  width?: string; // Ej. "100%"
  maxWidth?: string;
  maxTableBodyHeight?: string;
  minTableBodyHeight?: string;

  expandable?: boolean;
  renderExpanded?: (row: T) => React.ReactNode;
  renderExpandToggle?: (
    isExpanded: boolean,
    onClick: () => void,
    row: T
  ) => React.ReactNode;
  /**
   * Whether to render the built-in trailing expand/collapse toggle column. Default true.
   * Set false to keep the expand mechanism (rows still expand via a column's `renderCell`
   * receiving `toggleRowExpand`/`isExpanded`) while placing the toggle in your own column
   * — e.g. a dedicated fixed "Actions" column.
   */
  showExpandColumn?: boolean;

  /**
   * Secondary content shown inside the same table row (same `<tr>`): one `<td>`
   * spans all columns with a stacked layout — column cells on top, this content
   * full-width below (e.g. outage description).
   */
  renderRowBelow?: (row: T, rowIndex: number) => React.ReactNode;

  rowKey?: (row: T, index: number) => string | number;

  /**
   * NUEVO: Si deseas que la fila sea clickeable,
   * puedes pasar esta función. Se llamará al dar clic en una fila.
   */
  onRowClick?: (row: T, rowIndex: number) => void;

  /**
   * NUEVO: Si deseas resaltar filas seleccionadas,
   * pasa un Set con las keys de las filas (rowKey).
   */
  selectedRowKeys?: Set<string | number>;
  isFooterVisible?: boolean;
  overflow?: "visible" | "hidden"; // New prop for overflow control
  /**
   * Control horizontal scrolling behavior.
   * - "responsive": Mobile (<md) forces horizontal scroll, desktop (>=md) tries to fit
   * - "lg-fit": Below `lg` (laptop) forces horizontal scroll, `lg+` shrinks columns to fit
   * - "xl-fit": Below `xl` (desktop) forces horizontal scroll, `xl+` shrinks columns to fit
   * - "always": Always allow horizontal scroll (min-w-max)
   * - "never": Disable horizontal scroll (overflow-x-hidden)
   * Default: "responsive"
   */
  horizontalScroll?: "responsive" | "lg-fit" | "xl-fit" | "always" | "never";

  // Server-side pagination support
  serverSide?: boolean;
  totalCount?: number; // Total number of items on server
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  currentPage?: number; // Controlled current page for server-side
  rowsPerPage?: number; // Controlled rows per page for server-side
  
  // Loading state
  loading?: boolean;
  /** Skeleton row count while `loading` is true. Defaults to `rowsPerPage`. */
  loadingSkeletonRows?: number;
  /** Skeleton column count while `loading` is true. Defaults to `columns.length`. */
  loadingSkeletonCols?: number;
  /**
   * Control vertical alignment of cell content.
   * - "top": Content aligned to the top
   * - "center": Content vertically centered (default)
   * - "bottom": Content aligned to the bottom
   * Default: "center"
   */
  verticalAlign?: "top" | "center" | "bottom";
}

type SortDirection = "asc" | "desc";

// Función auxiliar para determinar el "sortKey"
function getColumnSortKey<T>(
  col: GenericTableColumn<T>,
  index: number
): string | null {
  if (col.id) return col.id;
  if (col.accessor) return String(col.accessor);
  if (typeof col.header === "string") return col.header;
  if (col.sortAccessor) return `__col_${index}`;
  return null;
}

type SortPrimitive =
  | string
  | number
  | Date
  | null
  | undefined
  | Array<string | number>;

function normalizeSortValue(v: SortPrimitive): {
  kind: "nil" | "number" | "date" | "string";
  value: number | string;
} {
  if (v == null) return { kind: "nil", value: 0 };

  // Arrays => join as a single comparable string
  if (Array.isArray(v)) {
    return {
      kind: "string",
      value: v
        .map((x) => String(x))
        .join(" ")
        .toLowerCase(),
    };
  }

  if (v instanceof Date && !isNaN(v.getTime())) {
    return { kind: "date", value: v.getTime() };
  }

  if (typeof v === "number" && Number.isFinite(v)) {
    return { kind: "number", value: v };
  }

  // Try to coerce numeric-looking strings
  const s = String(v).trim();
  const maybeNum = Number(s.replace(/,/g, ""));
  if (!Number.isNaN(maybeNum) && s !== "") {
    return { kind: "number", value: maybeNum };
  }

  return { kind: "string", value: s.toLowerCase() };
}

function compareSortValues(a: SortPrimitive, b: SortPrimitive): number {
  const A = normalizeSortValue(a);
  const B = normalizeSortValue(b);

  // nils always last (in ascending)
  if (A.kind === "nil" && B.kind === "nil") return 0;
  if (A.kind === "nil") return 1;
  if (B.kind === "nil") return -1;

  // numbers and dates compare numerically
  if (
    (A.kind === "number" || A.kind === "date") &&
    (B.kind === "number" || B.kind === "date")
  ) {
    return (A.value as number) - (B.value as number);
  }

  // strings compare lexicographically (case-insensitive already)
  return String(A.value).localeCompare(String(B.value), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/**
 * Hook to detect horizontal overflow in a container element
 * @param deps - Dependencies that should trigger recalculation (e.g., columns, data.length, rowsPerPage)
 * @returns [ref, hasOverflowX] - ref to attach to the container, boolean indicating if overflow exists
 */
function useHorizontalOverflow<T extends HTMLElement = HTMLDivElement>(
  deps: React.DependencyList
): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [hasOverflowX, setHasOverflowX] = useState(false);

  const checkOverflow = useCallback(() => {
    if (!ref.current) return;
    
    const element = ref.current;
    // Add 1px tolerance to account for rounding/subpixel rendering
    const hasOverflow = element.scrollWidth > element.clientWidth + 1;
    
    setHasOverflowX((prev) => {
      // Only update state if value changed to avoid unnecessary re-renders
      if (prev !== hasOverflow) {
        return hasOverflow;
      }
      return prev;
    });
  }, []);

  // Recalculate on mount and when deps change
  useLayoutEffect(() => {
    checkOverflow();
  }, [checkOverflow, ...deps]);

  // Listen to window resize
  useEffect(() => {
    const handleResize = () => {
      checkOverflow();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [checkOverflow]);

  // Use ResizeObserver to detect container size changes
  useEffect(() => {
    if (!ref.current) return;

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [checkOverflow]);

  return [ref, hasOverflowX];
}

export function GenericTable<T extends object>({
  columns,
  data,
  tableClassName = "",
  initialRowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 20, 50],
  width = "100%",
  maxWidth,
  maxTableBodyHeight = "400px",
  minTableBodyHeight,
  expandable = false,
  renderExpanded,
  renderExpandToggle,
  showExpandColumn = true,
  renderRowBelow,
  rowKey,
  onRowClick,
  selectedRowKeys,
  isFooterVisible = true,
  overflow = "hidden", // Default to hidden for backward compatibility
  horizontalScroll = "responsive", 
  serverSide = false,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  currentPage: controlledCurrentPage,
  rowsPerPage: controlledRowsPerPage,
  loading = false,
  loadingSkeletonRows,
  loadingSkeletonCols,
  verticalAlign = "center",
}: GenericTableProps<T>) {
  // ============== Paginación ==============
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [internalRowsPerPage, setInternalRowsPerPage] =
    useState(initialRowsPerPage);

  // Use controlled values if server-side, otherwise use internal state
  const currentPage =
    serverSide && controlledCurrentPage !== undefined
      ? controlledCurrentPage
      : internalCurrentPage;
  const rowsPerPage =
    serverSide && controlledRowsPerPage !== undefined
      ? controlledRowsPerPage
      : internalRowsPerPage;

  const setCurrentPage =
    serverSide && onPageChange ? onPageChange : setInternalCurrentPage;

  // ============== Ordenamiento ==============
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // ============== Filas expandidas ==============
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Check if maxTableBodyHeight is "100%" to use flexbox layout
  const useFlexLayout = maxTableBodyHeight === "100%";

  const handleSort = (col: GenericTableColumn<T>, index: number) => {
    const sortKey = getColumnSortKey(col, index);
    if (!sortKey) return;

    if (sortColumn === sortKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(sortKey);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  // Ordenar data - ensure data is always an array
  const sortedData = Array.isArray(data) ? [...data] : [];
  if (sortColumn) {
    // find the column using the same key generation the header used
    const colIndex = columns.findIndex(
      (c, idx) => getColumnSortKey(c, idx) === sortColumn
    );
    const colDef = columns[colIndex];

    if (colDef && (colDef.accessor || colDef.sortAccessor)) {
      sortedData.sort((a, b) => {
        const getVal = (row: T): SortPrimitive => {
          if (colDef.sortAccessor) return colDef.sortAccessor(row);
          if (colDef.accessor) return (row as any)[colDef.accessor];
          return null;
        };

        const result = compareSortValues(getVal(a), getVal(b));
        return sortDirection === "asc" ? result : -result;
      });
    }
  }

  // Paginación
  const totalRows =
    serverSide && totalCount !== undefined ? totalCount : sortedData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  // Keep currentPage within valid bounds whenever rowsPerPage or data length changes (only for client-side)
  useEffect(() => {
    if (!serverSide) {
      // totalPages can be 0; force at least page 1 for the UI
      const safeTotalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
      setInternalCurrentPage((p) => Math.min(p, safeTotalPages));
    }
  }, [totalRows, rowsPerPage, serverSide]);

  const handleFirstPage = () => {
    if (serverSide && onPageChange) {
      onPageChange(1);
    } else {
      setInternalCurrentPage(1);
    }
  };
  const handlePreviousPage = () => {
    const newPage = Math.max(1, currentPage - 1);
    if (serverSide && onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalCurrentPage(newPage);
    }
  };
  const handleNextPage = () => {
    const newPage = Math.min(totalPages, currentPage + 1);
    if (serverSide && onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalCurrentPage(newPage);
    }
  };
  const handleLastPage = () => {
    if (serverSide && onPageChange) {
      onPageChange(totalPages);
    } else {
      setInternalCurrentPage(totalPages);
    }
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    if (serverSide && onRowsPerPageChange) {
      onRowsPerPageChange(newRowsPerPage);
      if (onPageChange) {
        onPageChange(1); // Reset to first page when changing rows per page
      }
    } else {
      setInternalRowsPerPage(newRowsPerPage);
      setInternalCurrentPage(1);
    }
  };

  // Calculate startIndex for row indexing (needed for both server-side and client-side)
  const startIndex = serverSide ? 0 : (currentPage - 1) * rowsPerPage;

  // For server-side, use data as-is (already paginated). For client-side, slice the data
  const currentData = serverSide
    ? sortedData
    : (() => {
        const endIndex = startIndex + rowsPerPage;
        return sortedData.slice(startIndex, endIndex);
      })();

  // Expand/collapse
  const toggleRowExpand = (rowIndex: number) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(rowIndex)) {
      newSet.delete(rowIndex);
    } else {
      newSet.add(rowIndex);
    }
    setExpandedRows(newSet);
  };

  // Built-in trailing toggle column only when expandable AND not delegated to a custom column.
  const showToggleCol = expandable && showExpandColumn;
  const colCount = columns.length + (showToggleCol ? 1 : 0);
  // Always respect maxTableBodyHeight to prevent page scrolling - only allow "visible" if explicitly set
  const dynamicMaxHeight = maxTableBodyHeight || "400px";
  const effectiveOverflow = overflow || "hidden";
  const shouldConstrainHeight = effectiveOverflow !== "visible";
  const { leftOffsets, rightOffsets } = computeStickyOffsets(columns);

  const getFixedStylesForColumn = (
    colIdx: number,
    col: GenericTableColumn<T>
  ): React.CSSProperties => {
    if (!col.fixed) {
      return { width: col.width || "auto" };
    }
    return {
      position: "sticky",
      zIndex: 10,
      backgroundColor: "var(--color-semantic-bg-primary)",
      width: col.width || "auto",
      ...(col.fixed === "left"
        ? {
            left: leftOffsets[colIdx],
            borderRight: "1px solid var(--semantic-border-secondary)",
          }
        : {
            right: rightOffsets[colIdx],
            borderLeft: "1px solid var(--semantic-border-secondary)",
          }),
    };
  };

  const renderColumnBody = (
    col: GenericTableColumn<T>,
    colIdx: number,
    row: T,
    rowIndex: number,
    isExpanded?: boolean
  ): React.ReactNode => {
    if (col.renderCell) {
      return col.renderCell(row, rowIndex, toggleRowExpand, isExpanded);
    }
    if (col.accessor) {
      return String((row as any)[col.accessor]);
    }
    return null;
  };

  // Check if any column has a width defined to determine table layout
  const hasDefinedWidths = columns.some(col => col.width && col.width !== "auto");
    
  // When `renderRowBelow` is used the body row collapses into a single
  // `<td colSpan>` whose grid must align with the header `<th>`s. `table-fixed`
  // gives both an equal column distribution.
  const forceFixedLayout = Boolean(renderRowBelow);

  // Determine table layout classes based on horizontalScroll and widths
  const getTableLayoutClasses = () => {
    if (horizontalScroll === "never") {
      return {
        layout: forceFixedLayout || hasDefinedWidths ? "table-fixed" : "table-auto",
        minWidth: "",
        whitespace: hasDefinedWidths ? "" : "whitespace-nowrap",
      };
    }

    if (horizontalScroll === "always") {
      return {
        layout: forceFixedLayout ? "table-fixed" : "table-auto",
        minWidth: "min-w-max",
        whitespace: "whitespace-nowrap",
      };
    }

    if (horizontalScroll === "lg-fit") {
      return {
        layout: forceFixedLayout || hasDefinedWidths ? "table-fixed" : "table-auto",
        minWidth: "min-w-max lg:min-w-0",
        whitespace: "whitespace-nowrap lg:whitespace-normal",
      };
    }

    if (horizontalScroll === "xl-fit") {
      return {
        layout: forceFixedLayout || hasDefinedWidths ? "table-fixed" : "table-auto",
        minWidth: "min-w-max xl:min-w-0",
        whitespace: "whitespace-nowrap xl:whitespace-normal",
      };
    }
    return {
      layout: forceFixedLayout || hasDefinedWidths ? "table-fixed" : "table-auto",
      minWidth: "min-w-max",
      whitespace: "whitespace-nowrap",
    };
    
  };
  
  const tableClasses = getTableLayoutClasses();
  const tableLayoutClass = tableClasses.layout;
  const tableMinWidthClass = tableClasses.minWidth;
  const tableWhitespaceClass = tableClasses.whitespace;
  
  // Helper function to get wrap classes for a column
  const getWrapClasses = (col: GenericTableColumn<T>) => {
    if (col.wrapMode) {
      switch (col.wrapMode) {
        case "nowrap":
          return "whitespace-nowrap";
        case "truncate":
          return "truncate whitespace-nowrap overflow-hidden";
        case "wrap":
          return "whitespace-normal";
        case "break":
          return "whitespace-normal break-words";
        case "clamp-2":
          return "line-clamp-2 whitespace-normal overflow-hidden";
        default:
          return "";
      }
    }
    // Default behavior: if hasDefinedWidths, allow wrap, otherwise nowrap
    return hasDefinedWidths ? "break-words" : "";
  };

  // Helper function to get vertical alignment class
  const getVerticalAlignClass = () => {
    switch (verticalAlign) {
      case "center":
        return "align-middle";
      case "bottom":
        return "align-bottom";
      case "top":
      default:
        return "align-top";
    }
  };

  const verticalAlignClass = getVerticalAlignClass();

  // Helper function to get wrapper classes based on vertical alignment
  const getWrapperClasses = (col: GenericTableColumn<T>) => {
    const baseClasses = `${CELL_MIN_HEIGHT_CLASS} ${getWrapClasses(col)}`;
    
    // When verticalAlign is center or bottom, use flexbox to properly center/align content
    if (verticalAlign === "center") {
      return `${baseClasses} flex items-center`;
    }
    if (verticalAlign === "bottom") {
      return `${baseClasses} flex items-end`;
    }
    // For top alignment, keep default block behavior
    return baseClasses;
  };
    
  // Constant for consistent cell content wrapper height
  const CELL_MIN_HEIGHT_CLASS = "min-h-[2.5rem] leading-5";

  // Hook to detect horizontal overflow - recalculate when columns, data, or pagination changes
  const [scrollWrapperRef, hasOverflowX] = useHorizontalOverflow<HTMLDivElement>([
    columns.length,
    data.length,
    rowsPerPage,
    currentPage,
  ]);

  // Determine overflow classes based on horizontalScroll prop
  const getOverflowClasses = () => {
    if (horizontalScroll === "never") {
      return "overflow-x-hidden";
    }
    if (horizontalScroll === "always") {
      return "overflow-x-auto";
    }
    if (horizontalScroll === "lg-fit") {
      return "overflow-x-auto lg:overflow-x-hidden";
    }
    if (horizontalScroll === "xl-fit") {
      return "overflow-x-auto xl:overflow-x-hidden";
    }
    // responsive mode
    // Mobile: always overflow-x-auto
    // Desktop: overflow-x-auto only if hasOverflowX, otherwise overflow-x-hidden
    // NOTE: keep both branches as full literal class strings so Tailwind's
    // content scanner can detect `md:overflow-x-auto` / `md:overflow-x-hidden`.
    return hasOverflowX
      ? "overflow-x-auto md:overflow-x-auto"
      : "overflow-x-auto md:overflow-x-hidden";
  };

  return (
    <div className={`w-full ${useFlexLayout ? 'h-full flex flex-col overflow-hidden' : ''}`} style={{ maxWidth }}>
      <div className={`border border-semantic-border-secondary ${isFooterVisible ? 'rounded-t-sm' : 'rounded-sm'} shadow-sm bg-semantic-bg-primary ${useFlexLayout ? 'flex flex-col flex-1 min-h-0' : ''}`}
        style={{ width }}>
          <div
            ref={scrollWrapperRef}
            className={`
            ${getOverflowClasses()}
            custom-scrollbar relative ${useFlexLayout ? 'flex-1 min-h-0 h-full' : ''}
          `}>
        {/* Contenedor de scroll vertical y horizontal - always constrain height to prevent page scrolling */}
        <div
          className={`custom-scrollbar ${useFlexLayout ? 'h-full' : ''}`}
          style={{ 
            maxHeight: shouldConstrainHeight && !useFlexLayout ? dynamicMaxHeight : (useFlexLayout ? "100%" : "none"),
            minHeight: minTableBodyHeight || undefined,
            overflowY: useFlexLayout ? "auto" : (shouldConstrainHeight ? "auto" : "visible"),
            overflowX: "visible"
          }}
        >
          <table
            className={`${tableLayoutClass} ${tableMinWidthClass} ${tableWhitespaceClass} border-separate relative ${tableClassName}`}
            style={{ borderSpacing: 0, width: "100%" }}
          >
              {/* Cabecera */}
              <thead>
                <tr>
                  {columns.map((col, index) => {
                    const isSortable = Boolean(
                      col.accessor || col.sortAccessor
                    );
                    const colKey = getColumnSortKey(col, index);
                    const isActiveSort = colKey && sortColumn === colKey;

                    let svgClasses =
                      "w-3 h-3 text-gray-400 transition-transform";
                    if (isActiveSort) {
                      svgClasses = `
													w-3 h-3
													text-pink-600
													transition-transform
													${sortDirection === "desc" ? "rotate-180" : ""}
											`;
                    }
                    const thKey =
                      typeof col.header === "string"
                        ? col.header
                        : `col_${index}`;

                    const fixedStyles: React.CSSProperties = col.fixed
                      ? {
                          position: "sticky",
                          zIndex: 30, // Higher z-index for fixed header columns
                          backgroundColor: "var(--color-semantic-bg-secondary)",
                          width: col.width || "auto",
                          ...(col.fixed === "left"
                            ? {
                                left: leftOffsets[index],
                                borderRight:
                                  "1px solid var(--semantic-border-secondary)",
                              }
                            : {
                                right: rightOffsets[index],
                                borderLeft:
                                  "1px solid var(--semantic-border-secondary)",
                              }),
                        }
                      : {
                          // Ensure all sticky headers have a z-index lower than popovers (9999)
                          position: "sticky",
                          zIndex: 10,
                          width: col.width || "auto",
                        };

                    return (
                      <th
                        key={thKey}
                        className={`
														px-4 py-2
														text-left text-sm font-semibold
														text-semantic-text-secondary
														border-b border-semantic-border-secondary
														select-none bg-semantic-bg-primary
														sticky top-0
														${isSortable ? "cursor-pointer" : ""}
														${col.fixed ? "bg-semantic-bg-secondary" : ""}
												`}
                        style={fixedStyles}
                        onClick={() => isSortable && handleSort(col, index)}
                      >
                        <div className="flex items-center gap-1">
                          <span>{col.header}</span>
                          {isSortable && <IconUpDown className={svgClasses} />}
                        </div>
                      </th>
                    );
                  })}
                  {showToggleCol && (
                    <th
                      className="
													px-4 py-2
													text-left text-sm font-semibold
													text-semantic-text-secondary
													border-b border-semantic-border-secondary
													bg-semantic-bg-primary
													sticky top-0
											"
                      style={{ zIndex: 10 }}
                    >
                      {/* Col extra para expand/collapse */}
                    </th>
                  )}
                </tr>
              </thead>

              {/* Cuerpo */}
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={colCount} className="px-4 py-2">
                      <SkeletonTableRows
                        rows={loadingSkeletonRows ?? rowsPerPage}
                        cols={loadingSkeletonCols ?? columns.length}
                        rowHeight={30}
                        animated={true}
                      />
                    </td>
                  </tr>
                ) : currentData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={colCount}
                      className="
												px-4 py-2 text-center text-sm
												text-semantic-text-primary border-b
										"
                    >
                      No Data Available
                    </td>
                  </tr>
                ) : (
                  currentData.map((row, i) => {
                    const rowIndex = startIndex + i;
                    const isExpanded = expandedRows.has(rowIndex);
                    const keyForRow = rowKey ? rowKey(row, rowIndex) : rowIndex;
                    const isSelected = selectedRowKeys?.has(keyForRow);

                    const belowContent = renderRowBelow?.(row, rowIndex);
                    const renderBelowInsideRow =
                      Boolean(renderRowBelow) &&
                      belowContent != null &&
                      belowContent !== false;

                    // `group/row` lets sticky `fixed` cells listen to the
                    // row's hover state. Without it, fixed cells repaint
                    // their own opaque `bg-semantic-bg-primary` on top of
                    // the `<tr>` hover background and look "stuck" while
                    // the rest of the row shades — see the matching
                    // `group-hover/row:` classes on the fixed cells below.
                    const rowTrClassName = `
														group/row
														bg-semantic-bg-primary
														transition-colors
														hover:bg-semantic-state-active
                            align-top 
														${onRowClick ? "cursor-pointer" : ""}
														${isSelected ? "bg-semantic-state-active" : ""}
												`;

                    const expandedBlock =
                      expandable && isExpanded && renderExpanded ? (
                        <tr>
                          <td
                            colSpan={colCount}
                            className="
																border-b border-semantic-border-secondary
																px-4 py-1
														"
                          >
                            {renderExpanded(row)}
                          </td>
                        </tr>
                      ) : null;

                    if (renderBelowInsideRow) {
                      return (
                        <React.Fragment key={keyForRow}>
                          <tr
                            onClick={() => onRowClick?.(row, rowIndex)}
                            className={rowTrClassName}
                          >
                            <td
                              colSpan={colCount}
                              className="border-b border-semantic-border-secondary p-0 align-top"
                            >
                              <div className="flex min-w-0 flex-col">
                                <div
                                  className="grid w-full min-w-0 break-words"
                                  style={{
                                    gridTemplateColumns: showToggleCol
                                      ? `repeat(${columns.length}, minmax(0, 1fr)) auto`
                                      : `repeat(${columns.length}, minmax(0, 1fr))`,
                                  }}
                                >
                                  {columns.map((col, colIdx) => {
                                    const cellKey =
                                      typeof col.header === "string"
                                        ? col.header
                                        : `col_${colIdx}`;
                                    const fixedStyles = getFixedStylesForColumn(
                                      colIdx,
                                      col
                                    );
                                    return (
                                      <div
                                        key={cellKey}
                                        className={`
																		px-4 py-2
																		text-sm text-semantic-text-primary
                                    ${verticalAlignClass}
																		${col.fixed ? `${isSelected ? "bg-semantic-state-active" : "bg-semantic-bg-primary"} group-hover/row:bg-semantic-state-active transition-colors` : ""}
																`}
                                        style={fixedStyles}
                                      >
                                        <div className={getWrapperClasses(col)}>
                                          {renderColumnBody(
                                            col,
                                            colIdx,
                                            row,
                                            rowIndex,
                                            isExpanded
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {showToggleCol && (
                                    <div
                                      className={`
                              px-4 py-2
                              ${verticalAlignClass}
                          `}
                                    >
                                      {renderExpandToggle ? (
                                        renderExpandToggle(
                                          isExpanded,
                                          () => toggleRowExpand(rowIndex),
                                          row
                                        )
                                      ) : (
                                        <button
                                          type="button"
                                          className="
																		px-2 py-1
																		bg-transparent
																		hover:bg-semantic-state-active
																		rounded
																		text-xs font-medium
																"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleRowExpand(rowIndex);
                                          }}
                                        >
                                          {isExpanded ? "–" : "+"}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="w-full px-4 pb-2 pt-2 text-left text-xs leading-snug text-semantic-text-secondary">
                                  {belowContent}
                                </div>
                              </div>
                            </td>
                          </tr>
                          {expandedBlock}
                        </React.Fragment>
                      );
                    }

                    return (
                      <React.Fragment key={keyForRow}>
                        <tr
                          onClick={() => onRowClick?.(row, rowIndex)}
                          className={rowTrClassName}
                        >
                          {columns.map((col, colIdx) => {
                            const cellKey =
                              typeof col.header === "string"
                                ? col.header
                                : `col_${colIdx}`;

                            const fixedStyles = getFixedStylesForColumn(
                              colIdx,
                              col
                            );
                            return (
                              <td
                                key={cellKey}
                                className={`
																		px-4 py-2
																		text-sm text-semantic-text-primary
																		border-b border-semantic-border-secondary
                                    ${verticalAlignClass}
                                    
																		${col.fixed ? `${isSelected ? "bg-semantic-state-active" : "bg-semantic-bg-primary"} group-hover/row:bg-semantic-state-active transition-colors` : ""}
																`}
                                style={fixedStyles}
                              >
                                <div className={getWrapperClasses(col)}>
                                  {renderColumnBody(
                                    col,
                                    colIdx,
                                    row,
                                    rowIndex,
                                    isExpanded
                                  )}
                                </div>
                              </td>
                            );
                          })}

                          {showToggleCol && (
                            <td
                              className={`
                              px-4 py-2
                              border-b border-semantic-border-secondary
                              ${verticalAlignClass}
                          `}
                            >
                              {renderExpandToggle ? (
                                renderExpandToggle(
                                  isExpanded,
                                  () => toggleRowExpand(rowIndex),
                                  row
                                )
                              ) : (
                                <button
                                  type="button"
                                  className="
																		px-2 py-1
																		bg-transparent
																		hover:bg-semantic-state-active
																		rounded
																		text-xs font-medium
																"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRowExpand(rowIndex);
                                  }}
                                >
                                  {isExpanded ? "–" : "+"}
                                </button>
                              )}
                            </td>
                          )}
                        </tr>

                        {expandedBlock}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer => paginación */}
        {isFooterVisible && (
          <div
            className={`border-t border-semantic-border-secondary rounded-b-sm bg-semantic-bg-primary ${
              useFlexLayout ? "flex-shrink-0" : ""
            }`}
          >
            <div className="px-3 sm:px-4 py-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-semantic-text-primary text-xs sm:text-sm">
                  Total : {totalRows}
                </span>

                <div className="flex flex-wrap items-center justify-between gap-2 sm:justify-end text-semantic-text-primary">
                  <div className="flex items-center gap-1">
                    <button
                      className="border border-semantic-border-secondary rounded-md px-2 sm:px-3 py-1 hover:bg-semantic-state-active disabled:opacity-50 text-xs sm:text-sm"
                      onClick={handleFirstPage}
                      disabled={currentPage === 1}
                    >
                      <span className="text-sm sm:text-base">&laquo;</span>
                    </button>

                    <button
                      className="border border-semantic-border-secondary rounded-md px-2 sm:px-3 py-1 hover:bg-semantic-state-active disabled:opacity-50 text-xs sm:text-sm"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                    >
                      <span className="text-sm sm:text-base">&#8249;</span>
                    </button>

                    <span className="mx-1 text-sm">
                      {currentPage} of {totalPages || 1}
                    </span>

                    <button
                      className="border border-semantic-border-secondary rounded-md px-2 sm:px-3 py-1 hover:bg-semantic-state-active disabled:opacity-50 text-xs sm:text-sm"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages || totalPages === 0}
                    >
                      <span className="text-sm sm:text-base">&#8250;</span>
                    </button>

                    <button
                      className="border border-semantic-border-secondary rounded-md px-2 sm:px-3 py-1 hover:bg-semantic-state-active disabled:opacity-50 text-xs sm:text-sm"
                      onClick={handleLastPage}
                      disabled={currentPage === totalPages || totalPages === 0}
                    >
                      <span className="text-sm sm:text-base">&raquo;</span>
                    </button>
                  </div>

                  {/* Rows-per-page group */}
                  <div className="flex items-center gap-1">
                    <select
                      className="border border-semantic-border-secondary rounded-md px-2 py-1 bg-semantic-bg-primary text-xs sm:text-sm"
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      {rowsPerPageOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <span className="text-xs sm:text-sm hidden sm:inline">
                      Rows per page
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
